import { createContext, Dispatch, FC, useContext, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Children, ISnackbar } from '../constants/types/prop.types.ts';

interface ISnackbarContext {
  stack: ISnackbar[];
  pushSnackbar: Dispatch<Omit<ISnackbar, 'id'> & { single?: boolean }>;
  removeSnackbar: Dispatch<string>;
  clearStack: Dispatch<void>;
}

const SnackbarContext = createContext<ISnackbarContext>({
  stack: [],
  pushSnackbar: () => {},
  removeSnackbar: () => {},
  clearStack: () => {},
});

const SnackbarProvider: FC<Children> = ({ children }) => {
  const [snackbarStack, setStack] = useState<ISnackbar[]>([]);

  const pushSnackbar = (newSnackbar: Omit<ISnackbar, 'id'> & { single?: boolean }) => {
    if (newSnackbar.single) {
      setStack([{ id: uuidv4(), ...newSnackbar }]);
      return;
    }

    setStack((stack) => [...stack, { id: uuidv4(), ...newSnackbar }]);
  };

  const removeSnackbar = (id: string) => {
    setStack((stack) => stack.filter((snack) => snack.id !== id));
  };

  const clearStack = () => setStack([]);

  const value = useMemo(
    () => ({ stack: snackbarStack, pushSnackbar, clearStack, removeSnackbar }),
    [snackbarStack],
  );

  return <SnackbarContext.Provider value={value}>{children}</SnackbarContext.Provider>;
};

export default SnackbarProvider;

export const useSnackbarStack = () => useContext(SnackbarContext);
